import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchMovement, fetchValidation, fileUrl, deleteMovement, fetchWatches } from "@/lib/api";
import { formatYearRange, fmtMoney, statusTone } from "@/lib/format";
import MovementFormSheet from "@/components/MovementFormSheet";
import RelatedDocumentsPanel from "@/components/RelatedDocumentsPanel";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, ArrowUpRight, Pencil, ImageOff, Trash2, Watch as WatchIcon } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";

export default function MovementDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [matching, setMatching] = useState(null); // { total, items[] }

  const doDelete = async () => {
    try {
      await deleteMovement(id);
      toast.success("Movement deleted");
      nav("/movements");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete"));
    }
  };

  const loadMovement = useCallback(async () => {
    try {
      const d = await fetchMovement(id);
      setM(d);
    } catch {
      toast.error("Movement not found");
      nav("/movements");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { fetchValidation().then(setValidation); }, []);
  useEffect(() => { loadMovement(); }, [loadMovement]);
  // Pull the list of watches in inventory using this caliber so the user can
  // jump straight to any match without filtering the Inventory page by hand.
  useEffect(() => {
    if (!m?.movement_id) return;
    fetchWatches({ caliber: m.movement_id, limit: 100 })
      .then((r) => setMatching(r))
      .catch(() => setMatching({ total: 0, items: [] }));
  }, [m?.movement_id]);

  if (loading || !m) {
    return <div className="px-10 py-20 text-[#3A2A10]" data-testid="movement-detail-loading">Loading…</div>;
  }

  // Bridge side (image_path) = canonical thumbnail / left column.
  // Dial side (dial_side_path) = right column when present.
  const bridgeImg = fileUrl(m.image_path);
  const dialImg = fileUrl(m.dial_side_path);
  const years = formatYearRange(m.year_introduced, m.year_discontinued);

  return (
    <div className="px-6 md:px-10 py-8 pb-28 max-w-[1600px]" data-testid="movement-detail">
      <Link
        to="/movements"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3A2A10] hover:text-[#3A2A10] transition-colors mb-6"
        data-testid="back-to-movements"
      >
        <ArrowLeft size={14} /> All Movements
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[520px_1fr] gap-8 mb-8">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <ImagePane img={bridgeImg} label="Bridge Side" testid="movement-image-bridge" />
            <ImagePane img={dialImg} label="Dial Side" testid="movement-image-dial" />
          </div>
          <Button
            onClick={() => setEditOpen(true)}
            variant="outline"
            className="w-full bg-transparent border-[#8B6A1F]/45 hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm"
            data-testid="edit-movement-btn"
          >
            <Pencil size={14} className="mr-2" /> Edit Movement
          </Button>
          <Button
            onClick={() => setConfirmDelete(true)}
            variant="outline"
            className="w-full bg-transparent border-rose-500/40 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700 rounded-sm mt-2"
            data-testid="detail-delete-movement-btn"
          >
            <Trash2 size={14} className="mr-2" /> Delete Movement
          </Button>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-2">Caliber Reference</div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-mono-data text-4xl text-[#0A0604] leading-none" data-testid="detail-caliber">{m.movement_id}</h1>
            {years && <span className="font-mono-data text-[10px] text-[#3A2A10] uppercase tracking-wider border border-[#D4AF37]/20 px-2 py-1 rounded-sm">{years}</span>}
          </div>
          <div className="text-sm text-[#3A2A10] mb-8">{m.mvmnt_mfg || "—"}</div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 border-t border-[#8B6A1F]/35 pt-5 mb-5">
            <Spec label="Type" value={m.movement_type} />
            <Spec label="Size" value={m.size} />
            <Spec label="Jewels" value={m.jewels} mono />
            <Spec label="BPH" value={m.bph} mono />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-5 border-t border-[#8B6A1F]/35 pt-5 mb-5">
            <Spec label="Lift Angle" value={m.lift_angle} suffix="°" mono />
            <Spec label="Protection" value={m.protection} />
          </div>
          <div className="grid grid-cols-3 gap-5 border-t border-[#8B6A1F]/35 pt-5 mb-5">
            <Spec label="Hour hand" value={m.hour_hand_mm} suffix=" mm" mono />
            <Spec label="Minute hand" value={m.minute_hand_mm} suffix=" mm" mono />
            <Spec label="Second hand" value={m.second_hand_mm} suffix=" mm" mono />
          </div>

          {m.description && (
            <div className="border-t border-[#8B6A1F]/35 pt-5 mb-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">Description</div>
              <p className="text-sm text-[#0A0604] leading-relaxed">{m.description}</p>
            </div>
          )}

          <div className="border-t border-[#8B6A1F]/35 pt-5 mb-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">Reference</div>
            {m.source_url ? (
              <a
                href={m.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#D4AF37] hover:text-[#E5C158] underline break-all"
                data-testid="mvmnt-source-link"
              >
                {m.source_url}
              </a>
            ) : (
              <span className="text-sm text-[#3A2A10] italic">No reference set</span>
            )}
            <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
              <a
                href={`https://ranfft.org/caliber/index?CaliberSearch%5Bname%5D=${encodeURIComponent(m.movement_id)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3A2A10] hover:text-[#0A0604] underline-offset-2 hover:underline"
                data-testid="mvmnt-ranfft-search"
              >
                Search ranfft.org →
              </a>
              <a
                href={`https://emmywatch.com/db/movement_/${(m.mvmnt_mfg || "").toLowerCase().replace(/\s+/g, "-")}--${(m.movement_id || "").toLowerCase().replace(/\s+/g, "-")}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3A2A10] hover:text-[#0A0604] underline-offset-2 hover:underline"
                data-testid="mvmnt-emmywatch-search"
              >
                Search emmywatch.com →
              </a>
            </div>
          </div>

          {m.notes && (
            <div className="border-t border-[#8B6A1F]/35 pt-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">Notes</div>
              <p className="text-sm text-[#3A2A10] leading-relaxed whitespace-pre-wrap">{m.notes}</p>
            </div>
          )}
        </div>
      </div>

      <section className="mt-10 border-t border-[#8B6A1F]/35 pt-8">
        <RelatedDocumentsPanel
          context="movement"
          caliber={m.movement_id}
          manufacturer={m.mvmnt_mfg}
          heading="Reference Documents"
        />
      </section>

      {/* Matching watches — every watch in inventory using this caliber */}
      <section className="mt-12 border-t border-[#8B6A1F]/35 pt-8" data-testid="matching-watches-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-1">In Your Collection</div>
            <h2 className="font-serif-display text-2xl text-[#0A0604]">
              Watches Using {m.movement_id}
            </h2>
          </div>
          <div className="text-xs text-[#3A2A10] font-mono-data">
            {matching === null ? "…" : `${matching.total.toLocaleString()} match${matching.total === 1 ? "" : "es"}`}
          </div>
        </div>
        {matching === null ? (
          <div className="text-sm text-[#3A2A10] py-6">Loading…</div>
        ) : matching.total === 0 ? (
          <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 px-6 py-10 text-center" data-testid="matching-watches-empty">
            <WatchIcon size={20} className="mx-auto text-[#3A2A10] mb-2" />
            <div className="font-serif-display text-lg text-[#0A0604] mb-1">No watches with this caliber</div>
            <div className="text-xs text-[#3A2A10]">
              Add one from <Link to="/inventory" className="underline hover:text-[#0A0604]">Inventory</Link> and its caliber field will auto-pull these specs.
            </div>
          </div>
        ) : (
          <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] divide-y divide-[#8B6A1F]/20">
            {matching.items.map((w) => (
              <Link
                key={w.id}
                to={`/inventory/${w.id}`}
                className="flex flex-wrap items-center gap-4 px-5 py-3.5 hover:bg-[#2B2214]/[0.06] transition-colors group"
                data-testid={`matching-watch-${w.id}`}
              >
                <span className="font-mono-data text-[#3A2A10] text-sm min-w-[52px]">#{w.excel_id ?? "—"}</span>
                <div className="min-w-0 flex-1 basis-[60%] sm:basis-auto">
                  <div className="text-sm text-[#0A0604] truncate">
                    {w.brand || "Unbranded"}
                    <span className="text-[#3A2A10]"> · </span>
                    <span className="text-[#3A2A10]">{w.model || "—"}</span>
                  </div>
                  <div className="text-[11px] text-[#3A2A10] mt-0.5 truncate font-mono-data">
                    {[w.year_mfg, w.serial && `SN ${w.serial}`, w.bin_location && `Location ${w.bin_location}`]
                      .filter(Boolean).join(" · ")}
                  </div>
                </div>
                {w.status && (
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border rounded-sm whitespace-nowrap ${statusTone(w.status)}`}>
                    {w.status}
                  </span>
                )}
                <div className="text-right min-w-[85px]">
                  <div className="text-[10px] uppercase tracking-wider text-[#3A2A10]">All In</div>
                  <div className="font-mono-data text-sm text-[#0A0604]">{fmtMoney(w.all_in)}</div>
                </div>
                <ArrowUpRight size={13} className="text-[#3A2A10] group-hover:text-[#0A0604] transition-colors" />
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em] border-t border-[#8B6A1F]/35 pt-6 mt-8">
        Services and parts are tracked per watch, not per caliber.{" "}
        <Link to="/inventory" className="text-[#3A2A10] hover:underline" data-testid="link-to-inventory">
          Open Inventory →
        </Link>
      </div>

      <MovementFormSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        movement={m}
        validation={validation}
        onSaved={() => { setEditOpen(false); loadMovement(); }}
      />
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Delete caliber {m.movement_id}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">
              This removes the reference entry for <span className="text-[#0A0604] font-mono-data">{m.movement_id}</span> only.
              Watches that reference this caliber keep their record; only the library entry is deleted.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm" data-testid="movement-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="movement-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Spec({ label, value, mono, suffix = "" }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#3A2A10] mb-1">{label}</div>
      <div className={`text-[#0A0604] ${mono ? "font-mono-data" : ""}`}>
        {empty ? <span className="text-[#0A0604]">—</span> : `${value}${suffix}`}
      </div>
    </div>
  );
}

/** A labelled image pane. Shows the image if present, an ImageOff placeholder
 *  otherwise. Small caption below marks which side is which. */
function ImagePane({ img, label, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="relative aspect-[4/3] bg-[#F9F0D4] border border-[#8B6A1F]/35 overflow-hidden">
        {img ? (
          <img src={img} alt={label} className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#0A0604]/60">
            <ImageOff size={24} strokeWidth={1} />
            <span className="text-[9px] uppercase tracking-[0.25em] mt-1.5">No image</span>
          </div>
        )}
      </div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-[#3A2A10] text-center">{label}</div>
    </div>
  );
}
